import { io } from 'socket.io-client';
import { IncomeWsEvents } from './ws-events';

const socketURI = process.env.REACT_APP_SOCKET_BASE_URL || process.env.REACT_APP_BASE_URL;
console.log("Socket URL ", socketURI);

// Store socket connections per chatId
const socketConnections = new Map();
// Store message handlers per chatId
const messageHandlers = new Map();
// Store pending connection promises to prevent duplicate connections
const pendingConnections = new Map();

// Cleanup function to disconnect and remove a socket connection
const disconnectSocket = (chatId) => {
    const socket = socketConnections.get(chatId);
    if (socket) {
        console.info(`Disconnecting socket for chatId: ${chatId}`);
        // Remove all event listeners
        socket.removeAllListeners();
        // Disconnect the socket
        socket.disconnect();
        // Remove from map
        socketConnections.delete(chatId);
        messageHandlers.delete(chatId);
    }
    // Also remove any pending connections
    pendingConnections.delete(chatId);
};

export const getSocket = async (chatId, onMessageReceive) => {
    // If we already have a connected socket for this chatId, return it
    const existingSocket = socketConnections.get(chatId);
    if (existingSocket && existingSocket.connected) {
        // Update the message handler in case it changed
        messageHandlers.set(chatId, onMessageReceive);
        return existingSocket;
    }

    // If there's a pending connection for this chatId, return that promise
    const pendingConnection = pendingConnections.get(chatId);
    if (pendingConnection) {
        console.info(`Reusing pending connection for chatId: ${chatId}`);
        // Update the message handler
        messageHandlers.set(chatId, onMessageReceive);
        return pendingConnection;
    }

    // Disconnect any existing socket for this chatId (if disconnected but still in map)
    if (existingSocket) {
        disconnectSocket(chatId);
    }

    // Disconnect all other socket connections (only one active chat at a time)
    for (const [id, socket] of socketConnections.entries()) {
        if (id !== chatId && socket.connected) {
            console.info(`Disconnecting old socket for chatId: ${id}`);
            disconnectSocket(id);
        }
    }

    // Create new socket connection promise
    const connectionPromise = (async () => {
        const token = localStorage.getItem('token');
        const socket = io(socketURI, {
            autoConnect: true,
            auth: {
                token: token,
            },
            query: {
                chat_id: chatId,
            },
        });

        // Store the message handler
        messageHandlers.set(chatId, onMessageReceive);

        // Set up event listeners
        socket.on(IncomeWsEvents.connect, () => {
            console.info(`Connected to socket.io for chatId: ${chatId}`, socket);
            // Remove from pending connections once connected
            pendingConnections.delete(chatId);
        });

        socket.on(IncomeWsEvents.disconnect, () => {
            console.info(`Disconnected from socket.io for chatId: ${chatId}`, socket);
        });

        socket.on(IncomeWsEvents.message, (message) => {
            const handler = messageHandlers.get(chatId);
            if (handler) {
                handler(message);
            }
        });

        // Store the socket connection
        socketConnections.set(chatId, socket);
        
        return socket;
    })();

    // Store the pending connection promise
    pendingConnections.set(chatId, connectionPromise);

    // Remove from pending if connection fails
    connectionPromise.catch((error) => {
        console.error(`Failed to create socket connection for chatId: ${chatId}`, error);
        pendingConnections.delete(chatId);
        socketConnections.delete(chatId);
        throw error;
    });
    
    return connectionPromise;
};

// Function to disconnect a specific socket by chatId
export const disconnectSocketByChatId = (chatId) => {
    disconnectSocket(chatId);
};

// Function to disconnect all sockets (useful for logout)
export const disconnectAllSockets = () => {
    console.info('Disconnecting all socket connections');
    for (const chatId of socketConnections.keys()) {
        disconnectSocket(chatId);
    }
};
